import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import type { ListFilters, Severity, Status } from '@/models/issue';
import { SEVERITIES, STATUSES } from '@/models/issue';
import { useIssuesList } from '@/controllers/useIssues';
import { IssueRow } from './components/IssueRow';

export function ListView() {
  const [search, setSearch] = useSearchParams();

  const filters = useMemo<ListFilters>(
    () => ({
      limit: 100,
      status: (search.get('status') as Status) || undefined,
      severity: (search.get('severity') as Severity) || undefined,
      component: search.get('component') || undefined,
      q: search.get('q') || undefined,
    }),
    [search]
  );

  const { items, total, loading, error } = useIssuesList(filters);

  function update(key: string, value: string) {
    const next = new URLSearchParams(search);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearch(next);
  }

  return (
    <div className="max-w-5xl">
      <h1
        className="text-[32px] tracking-tight mb-2"
        style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, color: 'var(--text-body)' }}
      >
        Liste
      </h1>
      <p className="text-[13px] text-[var(--text-muted)] mb-8 font-mono">
        {total} issues · tenant arkturian
      </p>

      <div className="section-title">Filter</div>
      <div className="grid grid-cols-4 gap-6 mb-8">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[var(--text-muted)] mb-1">
            Status
          </label>
          <select value={filters.status || ''} onChange={(e) => update('status', e.target.value)}>
            <option value="">alle</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[var(--text-muted)] mb-1">
            Severity
          </label>
          <select value={filters.severity || ''} onChange={(e) => update('severity', e.target.value)}>
            <option value="">alle</option>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[var(--text-muted)] mb-1">
            Component
          </label>
          <input
            className="input"
            placeholder="cloud-v2"
            value={filters.component || ''}
            onChange={(e) => update('component', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[var(--text-muted)] mb-1">
            <Search size={11} className="inline mr-1" strokeWidth={1.8} />
            Search
          </label>
          <input
            type="search"
            className="input"
            placeholder="text-search…"
            value={filters.q || ''}
            onChange={(e) => update('q', e.target.value)}
          />
        </div>
      </div>

      <div className="section-title">Issues</div>
      {loading && (
        <div className="py-10 text-center text-[13px] text-[var(--text-muted)] font-mono">
          lädt…
        </div>
      )}
      {error && (
        <div className="p-3 text-[12px] border-l-2"
          style={{
            borderColor: 'var(--color-error)',
            background: 'var(--color-error-bg)',
            color: 'var(--color-error)',
          }}>
          {error}
        </div>
      )}
      {!loading && !error && items.length === 0 && (
        <div className="py-10 text-center text-[13px] text-[var(--text-muted)] border border-dashed border-[var(--border)]">
          keine issues passen zu den filtern
        </div>
      )}
      {!loading && !error && items.length > 0 && (
        <div>
          {items.map((i) => (
            <IssueRow key={i.id} issue={i} />
          ))}
        </div>
      )}
    </div>
  );
}
